import React, { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Loader2, Sparkles, FileText, Calendar, ChevronDown, ChevronRight, Download, AlertTriangle, Globe } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const DOC_TYPES = {
  sale: [
    { key: 'offer_letter', label: 'Offer Letter', icon: '📝', desc: 'Formal offer to the seller' },
    { key: 'sale_agreement', label: 'Sale Agreement', icon: '📄', desc: 'Memorandum of sale / purchase contract' },
    { key: 'deed', label: 'Transfer Deed', icon: '🏛️', desc: 'Property ownership transfer document' },
    { key: 'inventory', label: 'Inventory Report', icon: '📋', desc: 'Room-by-room condition checklist' },
  ],
  rent: [
    { key: 'tenancy_agreement', label: 'Tenancy Agreement', icon: '📄', desc: 'AST / Standard Lease for your country' },
    { key: 'inventory', label: 'Inventory Checklist', icon: '📋', desc: 'Move-in condition report' },
    { key: 'notice', label: 'Notice Letter', icon: '📝', desc: 'Formal notice for any purpose' },
  ],
};

const AISuccessPanel = ({ dealId, listingType, country }) => {
  const [guidance, setGuidance] = useState(null);
  const [guidanceLoading, setGuidanceLoading] = useState(false);
  const [question, setQuestion] = useState('');
  const [draftLoading, setDraftLoading] = useState(null);
  const [draftContent, setDraftContent] = useState(null);
  const [draftType, setDraftType] = useState(null);
  const [showDocs, setShowDocs] = useState(false);
  const [showGovt, setShowGovt] = useState(false);
  const [govtData, setGovtData] = useState(null);

  const getGuidance = async (q) => {
    setGuidanceLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/plg/deal-guidance`, {
        deal_id: dealId,
        question: q || question || undefined,
      });
      setGuidance(res.data);
      setQuestion('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to get guidance');
    } finally {
      setGuidanceLoading(false);
    }
  };

  const draftDocument = async (docType) => {
    setDraftLoading(docType);
    try {
      const res = await axios.post(`${API_URL}/api/plg/draft-document`, {
        deal_id: dealId,
        document_type: docType,
      });
      setDraftContent(res.data.document);
      setDraftType(docType);
      toast.success('Document drafted!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to draft document');
    } finally {
      setDraftLoading(null);
    }
  };

  const fetchGovtInfo = async () => {
    if (govtData) { setShowGovt(!showGovt); return; }
    try {
      const res = await axios.get(`${API_URL}/api/plg/govt-requirements/${country || 'UK'}/${listingType || 'sale'}`);
      setGovtData(res.data);
      setShowGovt(true);
    } catch {
      toast.error('Failed to load government info');
    }
  };

  const downloadDraft = () => {
    if (!draftContent) return;
    const blob = new Blob([draftContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${draftType || 'document'}_draft.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const availableDocs = DOC_TYPES[listingType] || DOC_TYPES.sale;

  return (
    <div className="space-y-4" data-testid="ai-success-panel">
      {/* AI Guidance Section */}
      <div className="luxury-card p-5 border-l-4 border-l-[#E07A3A]">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="h-5 w-5 text-[#E07A3A]" />
          <h3 className="font-semibold text-[#1A1A1A] text-sm tracking-wider uppercase">AI Customer Success</h3>
        </div>
        <p className="text-xs text-slate-400 mb-3">Ask anything about your deal — what's next, what documents you need, timelines, costs.</p>

        <div className="flex gap-2 mb-3">
          <input
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') getGuidance(); }}
            placeholder="e.g. What should I do next?"
            className="flex-1 h-10 px-3 text-sm border border-slate-200 bg-[#F8FAFC] text-[#1A1A1A] outline-none focus:border-[#E07A3A]"
            data-testid="guidance-input"
          />
          <Button onClick={() => getGuidance()} disabled={guidanceLoading} className="bg-[#E07A3A] text-white hover:bg-[#C4652E] h-10 px-4" data-testid="ask-ai-btn">
            {guidanceLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          </Button>
        </div>

        {/* Quick action buttons */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {['What should I do next?', 'What documents do I need?', 'How long will this take?', 'What are the costs?'].map(q => (
            <button key={q} type="button" onClick={() => getGuidance(q)}
              className="px-2.5 py-1 text-[11px] border border-slate-200 text-slate-500 hover:border-[#E07A3A] hover:text-[#E07A3A] transition-colors"
              data-testid={`quick-q-${q.split(' ')[1]}`}>
              {q}
            </button>
          ))}
        </div>

        {/* Guidance Response */}
        {guidance && (
          <div className="mt-3 p-4 bg-[#F8FAFC] border border-slate-200 max-h-64 overflow-y-auto" data-testid="guidance-response">
            <pre className="text-xs text-[#1A1A1A]/80 whitespace-pre-wrap font-sans leading-relaxed">{guidance.guidance}</pre>
            {guidance.next_actions?.length > 0 && (
              <div className="mt-3 pt-3 border-t border-slate-200">
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-bold mb-1">Next Action</p>
                {guidance.next_actions.map(a => (
                  <p key={a.milestone} className="text-xs text-[#E07A3A] font-semibold">{a.action}</p>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Document Drafting */}
      <div className="luxury-card p-5">
        <button onClick={() => setShowDocs(!showDocs)} className="w-full flex items-center justify-between" data-testid="toggle-docs-section">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-[#E07A3A]" />
            <h3 className="font-semibold text-[#1A1A1A] text-sm tracking-wider uppercase">AI Document Drafting</h3>
          </div>
          {showDocs ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
        </button>

        {showDocs && (
          <div className="mt-4 space-y-2">
            <p className="text-xs text-slate-400 mb-3">Generate professional document templates for your {country || 'UK'} {listingType === 'rent' ? 'tenancy' : 'property sale'}.</p>
            {availableDocs.map(doc => (
              <button
                key={doc.key}
                onClick={() => draftDocument(doc.key)}
                disabled={draftLoading === doc.key}
                className="w-full flex items-center gap-3 p-3 text-left border border-slate-200 hover:border-[#E07A3A]/30 hover:bg-[#FEF3EC]/30 transition-colors"
                data-testid={`draft-${doc.key}`}
              >
                <span className="text-xl flex-shrink-0">{doc.icon}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#1A1A1A]">{doc.label}</p>
                  <p className="text-xs text-slate-400">{doc.desc}</p>
                </div>
                {draftLoading === doc.key ? <Loader2 className="h-4 w-4 text-[#E07A3A] animate-spin flex-shrink-0" /> : <ChevronRight className="h-4 w-4 text-slate-300 flex-shrink-0" />}
              </button>
            ))}

            {/* Draft result */}
            {draftContent && (
              <div className="mt-3 p-4 bg-[#F8FAFC] border border-slate-200" data-testid="draft-result">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-bold text-[#1A1A1A] uppercase tracking-wider">{draftType}</p>
                  <button onClick={downloadDraft} className="flex items-center gap-1 text-xs text-[#E07A3A] hover:text-[#C4652E] font-semibold" data-testid="download-draft-btn">
                    <Download className="h-3 w-3" /> Download
                  </button>
                </div>
                <div className="max-h-48 overflow-y-auto">
                  <pre className="text-xs text-[#1A1A1A]/70 whitespace-pre-wrap font-sans leading-relaxed">{draftContent.slice(0, 2000)}{draftContent.length > 2000 ? '...' : ''}</pre>
                </div>
                <div className="mt-2 p-2 bg-amber-50 border border-amber-200 flex items-start gap-2">
                  <AlertTriangle className="h-3.5 w-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-[10px] text-amber-700">AI-generated template for guidance only. Have all documents reviewed by a qualified legal professional before signing.</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Government Requirements */}
      <div className="luxury-card p-5">
        <button onClick={fetchGovtInfo} className="w-full flex items-center justify-between" data-testid="toggle-govt-section">
          <div className="flex items-center gap-2">
            <Globe className="h-5 w-5 text-[#E07A3A]" />
            <h3 className="font-semibold text-[#1A1A1A] text-sm tracking-wider uppercase">Government Appointments ({country || 'UK'})</h3>
          </div>
          {showGovt ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
        </button>

        {showGovt && govtData && (
          <div className="mt-4 space-y-3">
            {govtData.appointments?.map((apt, idx) => (
              <div key={idx} className="p-3 border border-slate-200 bg-[#F8FAFC]">
                <div className="flex items-center gap-2 mb-1">
                  <Calendar className="h-4 w-4 text-[#E07A3A]" />
                  <p className="text-sm font-semibold text-[#1A1A1A]">{apt.name}</p>
                </div>
                <p className="text-xs text-slate-500 ml-6">{apt.purpose}</p>
                <p className="text-[10px] text-slate-400 ml-6 mt-1">When: {apt.when}</p>
                {apt.url && (
                  <a href={apt.url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-[#E07A3A] ml-6 hover:underline">
                    Visit official website
                  </a>
                )}
              </div>
            ))}
            {(!govtData.appointments || govtData.appointments.length === 0) && (
              <p className="text-xs text-slate-400">No specific government appointments required at this stage.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AISuccessPanel;
